import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, Save, ChevronDown, ChevronUp, ChevronRight, RefreshCw,
  Loader2, SearchCheck, CheckCircle2, AlertTriangle, Lightbulb
} from "lucide-react";
import { useAuth, apiCall } from "../App";
import { toast } from "sonner";

function IssueDiagnosis({ module = "relationship", isCoachView = false, viewAsUserId = null, onSave }) {
  const { token } = useAuth();
  const [painPoints, setPainPoints] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [results, setResults] = useState([]);
  const [hasResponse, setHasResponse] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const fetchData = useCallback(async () => {
    const uidParam = isCoachView && viewAsUserId ? `&user_id=${viewAsUserId}` : (viewAsUserId ? `&user_id=${viewAsUserId}` : "");
    const uidResultParam = viewAsUserId ? `?user_id=${viewAsUserId}&module=${module}` : `?module=${module}`;
    const [ppRes, resultRes] = await Promise.all([
      apiCall("GET", `/issue-diagnosis/pain-points?module=${module}`, null, token),
      apiCall("GET", `/issue-diagnosis/results${uidResultParam}`, null, token),
    ]);
    if (ppRes.success) setPainPoints(ppRes.data.pain_points || []);
    if (resultRes.success) {
      setHasResponse(resultRes.data.has_response);
      setResults(resultRes.data.selected || []);
      if (resultRes.data.has_response) {
        setSelectedIds(resultRes.data.selected.map(p => p.id));
      }
    }
    setLoading(false);
  }, [token, viewAsUserId, isCoachView]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const togglePainPoint = (id) => {
    if (isCoachView) return;
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await apiCall("POST", "/issue-diagnosis/save", { selected_ids: selectedIds, module }, token);
    if (res.success) {
      setResults(res.data.results || []);
      setHasResponse(true);
      toast.success(`Saved ${res.data.selected_count} issue-specific pain points`);
      if (onSave) onSave();
    } else {
      console.error("Issue diagnosis save failed:", res.error || res);
      toast.error(res.error?.detail || res.error || "Save failed. Please retry or contact support.");
    }
    setSaving(false);
  };

  const handleRetake = async () => {
    await apiCall("POST", "/issue-diagnosis/retake", { module }, token);
    setSelectedIds([]);
    setHasResponse(false);
    setResults([]);
    setShowTest(true);
    if (onSave) onSave();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-divine-gold/50" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" data-testid="issue-diagnosis-row">
      {/* LEFT: Diagnosis Test Card */}
      <div className={`p-4 rounded-xl border transition-all ${
        hasResponse ? "bg-emerald-500/5 border-emerald-500/20" : "bg-cosmic-light/5 border-divine-gold/10"
      }`} data-testid="issue-diagnosis-test-card">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${hasResponse ? "bg-emerald-500/10" : "bg-divine-gold/10"}`}>
            <SearchCheck className={`w-5 h-5 ${hasResponse ? "text-emerald-400" : "text-rose-400"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className={`text-sm font-heading truncate ${hasResponse ? "text-emerald-400" : "text-rose-400"}`}>DEEP DIVE ISSUE-SPECIFIC DIAGNOSIS</p>
              {hasResponse && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
            </div>
            <p className="text-xs text-chakra-crown/40 font-heading">Pain Point Identification</p>
            <p className="text-xs text-chakra-crown/50 mt-1.5 leading-relaxed line-clamp-2">
              Select which issues are the bone of contention in your relationship.
            </p>
            <span className="text-[10px] text-chakra-crown/30 mt-1 block">{painPoints.length} pain points</span>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <button onClick={() => setShowTest(!showTest)}
              className="p-1.5 rounded-lg hover:bg-divine-gold/10 transition-colors" data-testid="issue-diagnosis-toggle">
              {showTest
                ? <ChevronDown className={`w-4 h-4 ${hasResponse ? "text-emerald-400/40" : "text-divine-gold/40"}`} />
                : <ChevronRight className={`w-4 h-4 ${hasResponse ? "text-emerald-400/40" : "text-divine-gold/40"}`} />}
            </button>
            {hasResponse && !isCoachView && (
              <button onClick={handleRetake} disabled={saving}
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 transition-colors"
                data-testid="issue-diagnosis-retake-btn">
                <RefreshCw className="w-2.5 h-2.5" /> Retake
              </button>
            )}
          </div>
        </div>

        {/* Expandable pain point selection */}
        <AnimatePresence>
          {showTest && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="mt-3 pt-3 border-t border-divine-gold/10 space-y-3">
                <p className="text-chakra-crown/40 text-[10px]">
                  Select all that apply ({selectedIds.length} of {painPoints.length} selected)
                </p>
                <div className="space-y-1.5 max-h-[380px] overflow-y-auto pr-1">
                  {painPoints.map((pp) => {
                    const isSelected = selectedIds.includes(pp.id);
                    return (
                      <button
                        key={pp.id}
                        onClick={() => togglePainPoint(pp.id)}
                        disabled={isCoachView}
                        data-testid={`issue-pp-${pp.id}`}
                        className={`
                          w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all
                          border text-left
                          ${isSelected
                            ? "bg-rose-500/10 border-rose-500/30 text-rose-300"
                            : "bg-cosmic-void/40 border-divine-gold/10 text-chakra-crown/60 hover:border-divine-gold/25 hover:text-chakra-crown/80"
                          }
                          ${isCoachView ? "cursor-default" : "cursor-pointer"}
                        `}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                          isSelected ? "border-rose-400 bg-rose-500/20" : "border-divine-gold/20"
                        }`}>
                          {isSelected && <Check className="w-2.5 h-2.5 text-rose-400" />}
                        </div>
                        <span className="text-[10px] text-chakra-crown/30 shrink-0">{pp.id}.</span>
                        {pp.label}
                      </button>
                    );
                  })}
                </div>
                {!isCoachView && (
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSave}
                      disabled={selectedIds.length === 0 || saving}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30 text-xs disabled:opacity-50 transition-all"
                      data-testid="issue-diagnosis-save-btn"
                    >
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Save Diagnosis
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* RIGHT: Results Card */}
      <div className="rounded-xl border border-divine-gold/10 bg-cosmic-deep/30 overflow-hidden" data-testid="issue-diagnosis-results-card">
        <button
          onClick={() => setShowResults(!showResults)}
          className="w-full flex items-center justify-between px-4 py-3"
          data-testid="issue-diagnosis-results-toggle"
        >
          <span className="flex items-center gap-2"><SearchCheck className="w-4 h-4 text-violet-400" /><span className="text-divine-gold text-xs font-heading tracking-wider">DEEP DIVE ISSUE-SPECIFIC RESULT</span></span>
          <div className="flex items-center gap-2">
            {hasResponse && results.length > 0 && (
              <span className="text-chakra-crown/30 text-[10px]">{results.length} insights</span>
            )}
            {showResults
              ? <ChevronUp className="w-3.5 h-3.5 text-divine-gold/40" />
              : <ChevronDown className="w-3.5 h-3.5 text-divine-gold/40" />}
          </div>
        </button>
        <AnimatePresence>
          {showResults && (
            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
              <div className="px-4 pb-4">
                {!hasResponse || results.length === 0 ? (
                  <p className="text-chakra-crown/30 text-sm text-center py-4" data-testid="issue-no-results">
                    Select your pain points and save to reveal awareness insights
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1" data-testid="issue-awareness-list">
                    {results.map((r, idx) => (
                      <motion.div
                        key={r.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="rounded-lg bg-cosmic-void/30 border border-divine-gold/5 hover:border-divine-gold/15 transition-colors p-3"
                        data-testid={`issue-awareness-${r.id}`}
                      >
                        <div className="flex items-start gap-2 mb-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 text-rose-400/70 shrink-0 mt-0.5" />
                          <span className="text-rose-300/80 text-xs font-heading">{r.label}</span>
                        </div>
                        <div className="flex items-start gap-2 pl-5">
                          <Lightbulb className="w-3 h-3 text-divine-gold/60 shrink-0 mt-0.5" />
                          <p className="text-chakra-crown/70 text-xs leading-relaxed">{r.awareness}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default IssueDiagnosis;
